import { forwardRef, useRef } from 'react';
import { bool, string, element, func, arrayOf, shape } from 'prop-types';
import { FocusOn } from 'react-focus-on';
import { CSSTransition } from 'react-transition-group';
import '../../constants/culture.js';
import { FocusableProvider } from '../../hooks/useFocusable.js';
import { useTheme } from 'styled-components';
import 'react-error-boundary';
import '../../theme/globalStyles.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import '../../theme/base/index.js';
import '../../theme/baseFit/index.js';
import '../../theme/pureGym/index.js';
import '../../theme/fitnessWorld/index.js';
import { Button } from '../button/index.js';
import { IconClose } from '../icons/generated/Close.js';
import { MenuProvider, useMenu } from './menuContext.js';
import { NAV_VARIANTS } from './constants.js';
export { NAV_VARIANTS } from './constants.js';
import { Wrapper, DropdownContainer, GlobalNavWrapper, CloseButton, Overlay, MenuWrapper } from './styles.js';
import { MenuPanel } from './menuPanel.js';
import { BUTTON_VARIANTS } from '../button/constants.js';

const Container = _ref => {
  let {
    buttonProps,
    trigger,
    unmountOnExit,
    ...rest
  } = _ref;
  const {
    direction,
    isOpen,
    labels,
    menuData,
    menuLevel,
    options,
    prevMenuLevel,
    resetMenu,
    setIsOpen
  } = useMenu();
  const {
    placement,
    variant,
    width
  } = options;
  const isRtl = direction === 'rtl';
  const triggerRef = useRef(null);
  const {
    transitions
  } = useTheme();

  /** Are we moving back to a parent (up) or to a sub-menu (down)? */
  const menuDirection = menuLevel < prevMenuLevel ? 'up' : 'down';
  const isOddLevel = !!(menuLevel % 2);

  /** Set panel data based on menu state */
  const nextPanel = menuDirection === 'up' ? menuData[menuLevel + 1] : menuData[menuLevel - 1];
  const panelA = !isOddLevel ? menuData[menuLevel] : nextPanel;
  const panelB = isOddLevel ? menuData[menuLevel] : nextPanel;

  /**
   * Menu close - we reset the level separately in the transition
   * `onExited` callback to avoid a visible shift.
   */
  const closeMenu = () => {
    setIsOpen(false);
  };
  const onExited = () => {
    resetMenu();
    triggerRef.current?.focus();
  };
  const menuContent = /*#__PURE__*/jsxs(MenuWrapper, {
    width: width,
    children: [/*#__PURE__*/jsx(MenuPanel, {
      data: panelA,
      visible: !isOddLevel
    }), /*#__PURE__*/jsx(MenuPanel, {
      data: panelB,
      visible: isOddLevel
    })]
  });
  return /*#__PURE__*/jsxs(Wrapper, {
    children: [/*#__PURE__*/jsx(Button, {
      "aria-expanded": isOpen,
      "aria-haspopup": true,
      onClick: () => setIsOpen(!isOpen),
      ref: triggerRef,
      variant: BUTTON_VARIANTS.PLAIN,
      ...buttonProps,
      children: trigger
    }), /*#__PURE__*/jsx(FocusOn, {
      enabled: isOpen,
      onClickOutside: closeMenu,
      onEscapeKey: closeMenu,
      returnFocus: true,
      children: /*#__PURE__*/jsx(CSSTransition, {
        appear: true,
        in: isOpen,
        onExited: onExited,
        timeout: transitions.default,
        unmountOnExit: unmountOnExit,
        children: variant === NAV_VARIANTS.DROPDOWN ? /*#__PURE__*/jsx(DropdownContainer, {
          placement: placement,
          children: /*#__PURE__*/jsx("nav", {
            "aria-label": labels?.nav,
            ...rest,
            children: menuContent
          })
        }) : /*#__PURE__*/jsxs(GlobalNavWrapper, {
          isRtl: isRtl,
          children: [/*#__PURE__*/jsxs(CloseButton, {
            variant: BUTTON_VARIANTS.PLAIN,
            onClick: closeMenu,
            children: [/*#__PURE__*/jsx(IconClose, {
              "aria-hidden": true
            }), /*#__PURE__*/jsx("span", {
              children: labels.close
            })]
          }), /*#__PURE__*/jsx("nav", {
            "aria-label": labels?.nav,
            itemScope: true,
            itemType: "http://schema.org/SiteNavigationElement",
            ...rest,
            children: menuContent
          })]
        })
      })
    }), variant === NAV_VARIANTS.GLOBAL && /*#__PURE__*/jsx(CSSTransition, {
      appear: true,
      in: isOpen,
      timeout: transitions.default,
      unmountOnExit: true,
      mountOnEnter: true,
      children: /*#__PURE__*/jsx(Overlay, {})
    })]
  });
};
/**
 * Nav expects `data` to be an array of objects that
 * make up your menu. Objects with children display their
 * sub-menu onClick, otherwise they are just links or buttons.
 *
 * For RTL languages to animate correctly, the component uses the
 * `dir="rtl"` CSS selector, so you need to ensure it is present
 * on the `html` tag.
 */
const Nav = /*#__PURE__*/forwardRef(function Nav(_ref2, ref) {
  let {
    buttonProps,
    data,
    labels,
    placement = 'bottom-end',
    trigger,
    variant = NAV_VARIANTS.DROPDOWN,
    width = 'auto',
    unmountOnExit = false,
    ...rest
  } = _ref2;
  return /*#__PURE__*/jsx(MenuProvider, {
    data: data,
    labels: labels,
    options: {
      placement,
      variant,
      width
    },
    children: /*#__PURE__*/jsx(FocusableProvider, {
      children: /*#__PURE__*/jsx(Container, {
        ref: ref,
        trigger: trigger,
        buttonProps: buttonProps,
        unmountOnExit: unmountOnExit,
        ...rest
      })
    })
  });
});
const menuItemShape = {
  active: bool,
  divider: bool,
  href: string,
  icon: element,
  name: string.isRequired,
  onClick: func,
  primary: bool,
  rtl: bool,
  to: string,
  type: string
};

// Recursively set the children prop
menuItemShape.children = arrayOf(shape(menuItemShape));

export { Nav };
